import React from "react";
import {
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popover,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ArrowDropDown,
  Assignment,
  ErrorOutline,
} from "@mui/icons-material";
import { formatDateWithTime } from "../../helpers";
import { IActiviityProps, IActivityModuleInformation } from "./Activity.types";
import { useSelector } from "react-redux";
import { IStoreState } from "../../redux/initialStoreState";
import { IBusinessTask } from "../../redux/activity/activity.types";
import { LoadState } from "../../constants/enums";
import { useDispatchWrapper } from "../../hooks";
import {
  clearActivityState,
  fetchBusinessTasksAsync,
} from "../../redux/activity/activityActions";
import { CustomChip } from "../CustomChip";
import { ManageBusinessTask } from "./BusinessTasks/ManageBusinessTask";
import UploadFileIcon from "@mui/icons-material/UploadFile";

export const Activity: React.FC<IActiviityProps> = (props) => {
  const { task, moduleInformation } = props;
  const { data, dataLoading, error } = useSelector(
    (storeState: IStoreState) => storeState.acitivty.tasks
  );
  const theme = useTheme();
  const dispatch = useDispatchWrapper();

  React.useEffect(() => {
    dispatch(fetchBusinessTasksAsync(moduleInformation.moduleId));
    return () => {
      dispatch(clearActivityState());
    };
  }, []);

  return (
    <>
      {task && (
        <Stack mt={3}>
          <Stack
            padding={1}
            sx={{
              backgroundColor: theme.palette.grey[200],
              borderRadius: 1,
            }}
          >
            <Typography variant="h5" fontWeight={600}>
              Tasks
            </Typography>
          </Stack>
          <Stack direction={"column"} spacing={1} mt={3}>
            {dataLoading === LoadState.InProgress && (
              <>
                <Skeleton height={"30px"} />
                <Skeleton height={"30px"} />
                <Skeleton height={"30px"} />
                <Skeleton height={"30px"} />
                <Skeleton height={"30px"} />
              </>
            )}
            {dataLoading === LoadState.Failed && error && (
              <>
                <Box
                  minHeight={100}
                  display={"flex"}
                  flexDirection={"column"}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <Box display={"flex"} justifyContent={"center"} mb={1}>
                    <ErrorOutline />
                  </Box>
                  <Box display={"flex"} justifyContent={"center"} width={"80%"}>
                    <Typography
                      variant="body1"
                      textAlign={"center"}
                      fontWeight={600}
                    >
                      {error}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
            {data.map((item, index) => {
              return (
                <TaskRow
                  key={index}
                  task={item}
                  moduleInformation={moduleInformation}
                />
              );
            })}
          </Stack>
        </Stack>
      )}
    </>
  );
};

const TaskRow: React.FC<{
  task: IBusinessTask;
  moduleInformation: IActivityModuleInformation;
}> = ({ task, moduleInformation }) => {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);

  const colors = {
    High: "error",
    Medium: "warning",
    Low: "success",
  };
  return (
    <>
      <Stack
        position={"relative"}
        minHeight={"80px"}
        sx={{ cursor: "pointer" }}
        onClick={() => setOpen(true)}
      >
        <Stack
          direction={"row"}
          spacing={1.5}
          sx={{
           ":before": {
            content: "''",
            height: "100%",
            width: "2px",
            position: "absolute",
            left: "18px", // <-- fixed position for vertical line
            top: 0,
            bottom: 0,
            zIndex: 0,
              backgroundColor: theme.palette.error.main,
            },
          }}
        >
          <Box
            border={"1px solid #fff"}
            sx={{ backgroundColor: "#fff" }}
            zIndex={1}
            height={"fit-content"}
          >
            <Assignment color="primary" fontSize="large" />
          </Box>
          <Stack pl={1} width={"100%"}>
            <Stack
              direction={"row"}
              flexWrap={"wrap"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
             
                <Typography
                  variant="body1"
                  fontSize={"1.1rem"}
                  color={"primary"}
                  maxWidth={"55%"}
                  textOverflow={"ellipsis"}
                  overflow={"hidden"}
                  whiteSpace={"nowrap"}
                  fontWeight={600}
                >
                  {task.task_name}
                </Typography>
              
             
              <Typography variant="body1" fontWeight={600}>
                {formatDateWithTime(task.create_ts)}
              </Typography>
            </Stack>
            <Stack direction={"row"} justifyContent={"space-between"}>
              <Typography variant="body1" fontWeight={500}>
                You had a task.
              </Typography>
              <Stack direction={'row'} spacing={1}>
             {task.file_upload&& task.file_upload.length > 0 &&
              <Tooltip title="File uploaded">
              <UploadFileIcon />
              </Tooltip>

             }
              <CustomChip
                size="small"
                color={colors[task.task_priority as "Low"] as any}
                sx={{
                  fontWeight: 600,
                  color: task.task_priority === "Medium" ? "#000" : "#fff",
                }}
                content={task.task_priority}
              />
              </Stack>
            
            </Stack>
            {/* <Typography variant="body1" fontWeight={500}>
            Created By : <span style={{marginLeft: 2}}>{task.craeted_by_name}</span>
          </Typography> */}
          </Stack>
        </Stack>
      </Stack>
      {open && (
        <ManageBusinessTask
          open={open}
          task={task}
          moduleInformation={moduleInformation}
          onClose={() => setOpen(false)}
          onComplete={()=> {
            setOpen(false)
            }}
        />
      )}
    </>
  );
};

const ActivityMenuOption: React.FC<{
  icon: any;
  label: string;
  children?: {
    label: string;
    value?: string;
    menuItems: { label: string; icon: React.ReactNode; onClick: () => void }[];
  };
}> = ({ icon, label, children }) => {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const canBeOpen = open && Boolean(anchorEl);

  const id = canBeOpen ? "data-table-v2-search-transition-popper" : undefined;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen((previousOpen) => !previousOpen);
  };

  return (
    <>
      <Stack
        direction={"row"}
        sx={{ cursor: "pointer" }}
        alignItems={"center"}
        border={`1px solid ` + theme.palette.grey[500]}
        borderRadius={1}
        width={"100%"}
        spacing={0.5}
      >
        <Stack
          direction={"row"}
          pl={0.8}
          flex={2.5}
          alignItems={"center"}
          spacing={0.5}
        >
          {icon}
          <Typography variant="body2" fontWeight={500}>
            {label}
          </Typography>
        </Stack>
        <Stack
          flex={1}
          sx={{
            borderLeft: `1px solid ` + theme.palette.grey[500],

            height: 30,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: children ? 1 : 0.1,
            pointerEvents: children ? "auto" : "none",
          }}
          aria-describedby={id}
          onClick={handleClick}
        >
          <ArrowDropDown />
        </Stack>
      </Stack>
      {children && (
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          sx={{ zIndex: 1200 }}
          onClose={() => setOpen(false)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Paper variant="outlined" sx={{ minWidth: 250, pb: 1 }}>
            <Box padding={1}>
              <Typography variant="h4" fontWeight={600}>
                {children.label}
              </Typography>
              {children.value && (
                <Typography variant="body1" color="primary">
                  {children.value}
                </Typography>
              )}
            </Box>
            <Divider sx={{ mt: 1, mb: 1 }} />
            <List disablePadding>
              {children.menuItems.map((menuItem, index) => {
                return (
                  <ListItemButton
                    key={index}
                    sx={{ p: 0.8 }}
                    onClick={menuItem.onClick}
                  >
                    <ListItemIcon>{menuItem.icon}</ListItemIcon>
                    <ListItemText primary={menuItem.label} />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
        </Popover>
      )}
    </>
  );
};
